import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { LionWatermark } from "./LionWatermark";
import { STATS } from "../../data/content";
import { scrollToHash } from "@/lib/scrollToHash";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const ABOUT_VIDEO_SRC = "/videos/haydenvideo.mp4";
const LOOP_FADE_SECONDS = 0.8;

const AboutVideo = () => {
  const videoRef = useRef(null);
  const [faded, setFaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const { currentTime, duration } = video;
      if (!duration) return;
      setFaded(currentTime >= duration - LOOP_FADE_SECONDS);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={videoRef}
      src={ABOUT_VIDEO_SRC}
      muted
      loop
      playsInline
      preload="auto"
      aria-label="Blacklion Construction Group renovation walkthrough in Toronto"
      style={{ transitionDuration: `${LOOP_FADE_SECONDS * 1000}ms` }}
      className={`absolute inset-0 h-full w-full object-cover transition-opacity ease-in-out ${
        faded ? "opacity-0" : "opacity-100"
      }`}
    />
  );
};

const useCountUp = (target, active, duration = 1350) => {
  const [value, setValue] = useState(0);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!active || startedRef.current) return;
    startedRef.current = true;

    let raf;
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setValue(Math.round(target * easeOutCubic(progress)));
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      } else {
        setValue(target);
        setDone(true);
      }
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);

  return [value, done];
};

const StatItem = ({ stat, active }) => {
  const isNumeric = typeof stat.value === "number";
  const [count, done] = useCountUp(isNumeric ? stat.value : 0, active && isNumeric);

  return (
    <div data-testid={`stat-${stat.label.split(" ")[0].toLowerCase()}`}>
      <b
        className={`font-hero block text-lg font-semibold text-white transition-all duration-500 sm:text-2xl lg:text-3xl ${
          !isNumeric ? (active ? "translate-y-0 scale-100 opacity-100" : "translate-y-1 scale-95 opacity-0") : ""
        }`}
      >
        {isNumeric ? `${count}${done ? stat.suffix ?? "" : ""}` : stat.value}
      </b>
      <span className="my-1 block h-[2px] w-3 bg-[#c9a24d] sm:my-2 sm:w-4" />
      <i className="block text-[9px] font-semibold not-italic uppercase leading-snug tracking-wider text-neutral-400 sm:text-[10px]">
        {stat.label}
      </i>
    </div>
  );
};

const StatsOverlay = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid="about-stats-overlay"
      className="absolute inset-x-0 bottom-0 grid grid-cols-2 gap-x-4 gap-y-2 bg-gradient-to-t from-black/95 via-black/70 to-transparent px-4 py-3 backdrop-blur-[2px] sm:grid-cols-4 sm:gap-x-6 sm:gap-y-5 sm:px-6 sm:py-6"
    >
      {STATS.map((s) => (
        <StatItem key={s.label} stat={s} active={active} />
      ))}
    </div>
  );
};

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative overflow-hidden border-t border-[#c9a24d]/25 bg-[#0a0a0a]"
    >
      <div className="grid lg:grid-cols-2">
        <Reveal className="relative flex flex-col justify-center overflow-hidden px-6 py-24 lg:px-16 lg:py-32">
          <LionWatermark className="-bottom-[8%] -right-[6%] h-[85%] sm:h-[75%]" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">
            Who We Are
          </span>
          <h2 className="font-hero mt-4 text-3xl font-semibold leading-tight tracking-normal text-white md:text-4xl lg:text-5xl">
            A construction group built on{" "}
            <span className="gold-gradient-text italic">reputation</span>, not shortcuts.
          </h2>

          <div className="mt-6 max-w-[480px] space-y-5 text-base leading-relaxed text-neutral-400">
            <p>
              Blacklion Construction Group Inc. is a premium general contractor
              based in Toronto, proudly serving Etobicoke, Scarborough, North
              York, Mississauga, Oakville and the wider Greater Toronto Area.
              We partner with homeowners, architects and designers to deliver
              custom homes, meticulous renovations and additions that
              stand the test of time.
            </p>
            <p>
              A standard of quality, reliability and craftsmanship in every
              detail.
            </p>
          </div>

          <div className="my-8 h-[2px] w-11 bg-[#c9a24d]" />

          <a
            href="#projects"
            onClick={(e) => scrollToHash(e, "#projects")}
            data-testid="about-cta-story"
            className="group inline-flex w-fit items-center gap-2 border border-gold/50 px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-gold transition-all hover:border-gold hover:bg-gold hover:text-black"
          >
            Our Story
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </a>
        </Reveal>

        <Reveal delay={0.15} className="relative min-h-[420px] overflow-hidden lg:min-h-full">
          <AboutVideo />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />
          <StatsOverlay />
        </Reveal>
      </div>
    </section>
  );
};
